var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from "react";
import { Header } from "@/components/shared/Header";
import { Footer } from "@/components/shared/Footer";
import { BottomNav } from "@/components/shared/BottomNav";
import { GlossaryQuiz } from "@/components/quiz/GlossaryQuiz";
import { CategoriesSheet } from "@/components/shared/CategoriesSheet";
import { AISearchSheet } from "@/components/ai/AISearchSheet";
import { KitchenHelperChat } from "@/components/ai/KitchenHelperChat";
import { CatalogLayout } from "@/components/catalog/CatalogLayout";
import { CatalogFilters } from "@/components/catalog/CatalogFilters";
import { CatalogGrid } from "@/components/catalog/CatalogGrid";
import { CatalogCard } from "@/components/catalog/CatalogCard";
import { CatalogPagination } from "@/components/catalog/CatalogPagination";
import { RecipeCardStack } from "@/components/catalog/RecipeCardStack";
import { ActiveFilterChips } from "@/components/catalog/ActiveFilterChips";
import { QuickViewModal } from "@/components/catalog/QuickViewModal";
import { fetchRecipeIndex, loadRecipe } from "@/utils/recipeApi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Clock, Users, Shuffle, Flame } from "lucide-react";
export function RecipeCatalog({ onNavigate, menuItems, footerMenuItems, siteName }) {
    // Recipe data state
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [sortBy, setSortBy] = useState("newest");
    const [currentPage, setCurrentPage] = useState(1);
    const [showCardStack, setShowCardStack] = useState(false);
    const [showQuickView, setShowQuickView] = useState(false);
    const [quizOpen, setQuizOpen] = useState(false);
    const [categoriesSheetOpen, setCategoriesSheetOpen] = useState(false);
    const handleCategoriesClick = () => {
        setCategoriesSheetOpen(true);
    };
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [loadingFullRecipe, setLoadingFullRecipe] = useState(false);
    const [aiSearchOpen, setAiSearchOpen] = useState(false);
    const [kitchenHelperOpen, setKitchenHelperOpen] = useState(false);
    const itemsPerPage = 9;
    // Load recipes from API
    useEffect(() => {
        function loadRecipes() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    setLoading(true);
                    setError(null);
                    // Use TypeScript function directly instead of window.loadRecipeIndex
                    const response = yield fetchRecipeIndex({
                        per_page: 100,
                        page: 1,
                        orderby: 'title',
                        order: 'ASC'
                    });
                    console.log('[RecipeCatalog] API response:', response);
                    if (response && response.items) {
                        console.log('[RecipeCatalog] Found', response.items.length, 'recipes');
                        // Transform API response to Recipe format
                        const transformedRecipes = response.items.map(item => {
                            var _a;
                            const flameToDifficulty = {
                                1: 'easy',
                                2: 'medium',
                                3: 'hard'
                            };
                            // Extract image URL from object or use string directly
                            const imageUrl = typeof item.image === 'object' && item.image
                                ? (item.image.full || item.image.catalog || item.image.thumbnail || '')
                                : (item.image || '');
                            // Ensure id and slug are strings
                            // Prefer slug over recipe_id for navigation
                            const recipeId = String(item.recipe_id || item.slug || '');
                            const recipeSlug = String(item.slug || item.recipe_id || '');
                            // Log warning if slug is missing (using recipe_id as fallback)
                            if (!item.slug && item.recipe_id) {
                                console.warn('[RecipeCatalog] Recipe missing slug field, using recipe_id as fallback:', {
                                    title: item.title,
                                    recipe_id: item.recipe_id,
                                    finalSlug: recipeSlug
                                });
                            }
                            return {
                                id: recipeId,
                                slug: recipeSlug,
                                title: item.title || '',
                                description: item.summary || '',
                                cookTime: item.cook_time_total_minutes || 0,
                                servings: item.yield ? parseInt(((_a = item.yield.match(/\d+/)) === null || _a === void 0 ? void 0 : _a.join('')) || '4', 10) : 4,
                                difficulty: flameToDifficulty[item.flame || 1] || 'easy',
                                categories: item.category ? [item.category] : [],
                                mealTypes: [], // Not available in API summary
                                ingredients: [], // Not available in API summary
                                instructions: [], // Not available in API summary
                                image: imageUrl,
                            };
                        });
                        setRecipes(transformedRecipes);
                        // Also store in window for compatibility
                        window.__recipeCatalogApiData = transformedRecipes;
                        // Don't treat empty database as an error - it's a valid state
                        // The component will show a friendly empty state message
                        if (transformedRecipes.length === 0) {
                            console.info('[RecipeCatalog] No recipes found in database - showing empty state');
                            // Don't set error - let the normal render handle empty state
                        }
                    }
                    else {
                        console.error('[RecipeCatalog] Invalid API response:', response);
                        setError('Invalid response from API');
                    }
                }
                catch (err) {
                    console.error('[RecipeCatalog] Error loading recipes:', err);
                    setError(err instanceof Error ? err.message : 'Failed to load recipes');
                    // Fallback to window data if available
                    if (window.__recipeCatalogApiData && window.__recipeCatalogApiData.length > 0) {
                        setRecipes(window.__recipeCatalogApiData);
                    }
                }
                finally {
                    setLoading(false);
                }
            });
        }
        loadRecipes();
    }, []);
    // Calculate filter counts dynamically from recipes
    const filterGroups = useMemo(() => {
        // Count categories
        const categoryCounts = {};
        const difficultyCounts = { easy: 0, medium: 0, hard: 0 };
        const cookTimeCounts = { quick: 0, medium: 0, long: 0 };
        recipes.forEach(recipe => {
            // Count categories
            recipe.categories.forEach(cat => {
                if (cat) {
                    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                }
            });
            // Count difficulty (flame)
            if (recipe.difficulty === 'easy')
                difficultyCounts.easy++;
            else if (recipe.difficulty === 'medium')
                difficultyCounts.medium++;
            else if (recipe.difficulty === 'hard')
                difficultyCounts.hard++;
            // Count cook time
            if (recipe.cookTime < 30)
                cookTimeCounts.quick++;
            else if (recipe.cookTime >= 30 && recipe.cookTime <= 60)
                cookTimeCounts.medium++;
            else if (recipe.cookTime > 60)
                cookTimeCounts.long++;
        });
        // Build category options from actual categories found
        const categoryOptions = Object.entries(categoryCounts)
            .map(([id, count]) => ({
            id,
            label: id.charAt(0).toUpperCase() + id.slice(1).replace(/-/g, ' '),
            count
        }))
            .sort((a, b) => a.label.localeCompare(b.label));
        return [
            {
                id: "category",
                label: "Category",
                options: categoryOptions.length > 0 ? categoryOptions : [],
            },
            {
                id: "difficulty",
                label: "Flame",
                options: [
                    { id: "easy", label: "", count: difficultyCounts.easy },
                    { id: "medium", label: "", count: difficultyCounts.medium },
                    { id: "hard", label: "", count: difficultyCounts.hard },
                ],
            },
            {
                id: "cookTime",
                label: "Cook Time",
                options: [
                    { id: "quick", label: "Under 30 mins", count: cookTimeCounts.quick },
                    { id: "medium", label: "30-60 mins", count: cookTimeCounts.medium },
                    { id: "long", label: "Over 60 mins", count: cookTimeCounts.long },
                ],
            },
        ];
    }, [recipes]); // Recalculate when recipes change
    const handleFilterChange = (groupId, optionId, checked) => {
        setSelectedFilters((prev) => {
            const groupFilters = prev[groupId] || [];
            const updated = checked
                ? [...groupFilters, optionId]
                : groupFilters.filter((id) => id !== optionId);
            return Object.assign(Object.assign({}, prev), { [groupId]: updated });
        });
        setCurrentPage(1); // Reset to first page on filter change
    };
    const handleClearAll = () => {
        setSelectedFilters({});
        setCurrentPage(1);
    };
    // Filter recipes
    const filteredRecipes = recipes.filter((recipe) => {
        var _a, _b, _c;
        // Category filter
        if (((_a = selectedFilters.category) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            if (!selectedFilters.category.some((cat) => recipe.categories.includes(cat))) {
                return false;
            }
        }
        // Difficulty filter
        if (((_b = selectedFilters.difficulty) === null || _b === void 0 ? void 0 : _b.length) > 0) {
            if (!selectedFilters.difficulty.includes(recipe.difficulty)) {
                return false;
            }
        }
        // Cook time filter
        if (((_c = selectedFilters.cookTime) === null || _c === void 0 ? void 0 : _c.length) > 0) {
            const hasQuick = selectedFilters.cookTime.includes("quick") && recipe.cookTime < 30;
            const hasMedium = selectedFilters.cookTime.includes("medium") &&
                recipe.cookTime >= 30 &&
                recipe.cookTime <= 60;
            const hasLong = selectedFilters.cookTime.includes("long") && recipe.cookTime > 60;
            if (!hasQuick && !hasMedium && !hasLong) {
                return false;
            }
        }
        return true;
    });
    // Sort recipes (client-side sorting)
    // Note: API already returns sorted by title by default
    const sortedRecipes = [...filteredRecipes].sort((a, b) => {
        switch (sortBy) {
            case "title":
                return a.title.localeCompare(b.title);
            case "cookTime":
                return a.cookTime - b.cookTime;
            case "newest":
            default:
                // For "newest", keep original order (API returns in insertion order)
                return 0;
        }
    });
    // Paginate
    const totalPages = Math.ceil(sortedRecipes.length / itemsPerPage);
    const paginatedRecipes = sortedRecipes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const difficultyBadgeMap = {
        easy: "Easy",
        medium: "Medium",
        hard: "Hard",
    };
    const difficultyColors = {
        easy: "#a8c5a4",
        medium: "#f5d77e",
        hard: "#c85a3f",
    };
    const getDifficultyBadge = (difficulty) => {
        const flameCount = difficulty === "easy" ? 1 : difficulty === "medium" ? 2 : 3;
        return (_jsx("div", { className: "px-2.5 py-1.5 rounded-full border-2 flex items-center gap-0.5", style: {
                borderColor: '#4a4a4a',
                backgroundColor: '#4a4a4a10',
            }, children: Array.from({ length: flameCount }).map((_, i) => (_jsx(Flame, { className: "w-3.5 h-3.5 text-[#4a4a4a]" }, i))) }));
    };
    const handleLikeRecipe = (recipe) => {
        console.log("Saved recipe:", recipe.title);
        // TODO: Add to favorites/saved recipes
    };
    const handleQuickView = (recipe) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        // If recipe already has ingredients/instructions, use it directly
        if (recipe.ingredients && recipe.ingredients.length > 0 && recipe.instructions && recipe.instructions.length > 0) {
            setSelectedRecipe(recipe);
            setShowQuickView(true);
            return;
        }
        // Otherwise, load full recipe data
        setLoadingFullRecipe(true);
        try {
            // Use TypeScript loadRecipe function (same as RecipeDetail)
            const recipeIdentifier = recipe.id; // API accepts recipe_id or slug
            if (recipeIdentifier) {
                const fullRecipeData = yield loadRecipe(recipeIdentifier);
                if (!fullRecipeData) {
                    console.warn('[RecipeCatalog] Could not load full recipe data');
                    setSelectedRecipe(recipe);
                    setShowQuickView(true);
                    return;
                }
                // Extract ingredients based on flame level (default to 1f)
                const flame = recipe.difficulty === 'easy' ? 1 : recipe.difficulty === 'medium' ? 2 : 3;
                const ingredientsKey = `ingredients_${flame}f`;
                const ingredientsData = fullRecipeData[ingredientsKey];
                // Format ingredients as strings - handle both 'item' and 'ingredient' field names
                const ingredients = ingredientsData
                    ? ingredientsData.map(ing => {
                        const parts = [];
                        if (ing.amount && ing.amount > 0) {
                            parts.push(ing.amount.toString());
                        }
                        if (ing.unit) {
                            parts.push(ing.unit);
                        }
                        // Try both 'item' and 'ingredient' field names
                        const itemName = ing.item || ing.ingredient || '';
                        if (itemName) {
                            parts.push(itemName);
                        }
                        if (ing.note) {
                            parts.push(`(${ing.note})`);
                        }
                        return parts.join(' ');
                    })
                    : [];
                // Extract instructions from steps based on flame level
                const stepsKey = `steps_${flame}f`;
                const stepsData = fullRecipeData[stepsKey];
                // Format instructions as strings (use method if available, fallback to instruction)
                const instructions = stepsData
                    ? stepsData.map(step => step.method || step.instruction)
                    : [];
                // Extract image URL from object or use string directly
                const fullImageUrl = typeof fullRecipeData.image === 'object' && fullRecipeData.image
                    ? (fullRecipeData.image.full || fullRecipeData.image.catalog || fullRecipeData.image.thumbnail || '')
                    : (fullRecipeData.image || recipe.image || '');
                // Ensure id and slug are strings
                const fullRecipeId = String(fullRecipeData.recipe_id || fullRecipeData.slug || recipe.id);
                const fullRecipeSlug = String(fullRecipeData.slug || recipe.slug || recipe.id);
                // Transform the full recipe data to match Recipe interface
                const transformedRecipe = {
                    id: fullRecipeId,
                    slug: fullRecipeSlug,
                    title: fullRecipeData.title || recipe.title,
                    description: fullRecipeData.summary || recipe.description,
                    cookTime: fullRecipeData.cook_time_total_minutes || recipe.cookTime,
                    servings: fullRecipeData.yield ? parseInt(((_a = fullRecipeData.yield.match(/\d+/)) === null || _a === void 0 ? void 0 : _a.join('')) || '4', 10) : recipe.servings,
                    difficulty: recipe.difficulty, // Keep existing difficulty mapping
                    categories: fullRecipeData.category ? [fullRecipeData.category] : recipe.categories,
                    mealTypes: recipe.mealTypes,
                    image: fullImageUrl,
                    ingredients: ingredients,
                    instructions: instructions,
                };
                setSelectedRecipe(transformedRecipe);
                setShowQuickView(true);
            }
            else {
                // Fallback: use existing recipe data
                setSelectedRecipe(recipe);
                setShowQuickView(true);
            }
        }
        catch (error) {
            console.error('[RecipeCatalog] Error loading full recipe:', error);
            // Fallback: use existing recipe data even if full load fails
            setSelectedRecipe(recipe);
            setShowQuickView(true);
        }
        finally {
            setLoadingFullRecipe(false);
        }
    });
    // Loading state
    if (loading) {
        return (_jsxs(_Fragment, { children: [_jsx(Header, { onNavigate: onNavigate, menuItems: menuItems, siteName: siteName, onQuizClick: () => setQuizOpen(true), onSearchClick: () => setAiSearchOpen(true), onKitchenHelperClick: () => setKitchenHelperOpen(true) }), _jsx(CatalogLayout, { title: "Recipe Catalog", description: "Loading recipes...", resultsCount: 0, children: _jsx("div", { className: "text-center py-12", children: _jsx("p", { className: "text-[#4a4a4a]/70", children: "Loading recipes..." }) }) }), _jsx(Footer, { menuItems: footerMenuItems, siteName: siteName })] }));
    }
    // Error state
    if (error) {
        return (_jsxs(_Fragment, { children: [_jsx(Header, { onNavigate: onNavigate, menuItems: menuItems, siteName: siteName, onQuizClick: () => setQuizOpen(true), onSearchClick: () => setAiSearchOpen(true), onKitchenHelperClick: () => setKitchenHelperOpen(true) }), _jsx(CatalogLayout, { title: "Recipe Catalog", description: "Error loading recipes", resultsCount: 0, children: _jsxs("div", { className: "text-center py-12", children: [_jsx("p", { className: "text-[#4a4a4a]/70 text-red-600 mb-4", children: error }), _jsx(Button, { onClick: () => window.location.reload(), className: "mt-4", children: "Reload Page" })] }) }), _jsx(Footer, { menuItems: footerMenuItems, siteName: siteName })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(Header, { onNavigate: onNavigate, menuItems: menuItems, siteName: siteName, onQuizClick: () => setQuizOpen(true), onSearchClick: () => setAiSearchOpen(true), onKitchenHelperClick: () => setKitchenHelperOpen(true) }), _jsxs(CatalogLayout, { title: "Recipe Catalog", description: "Browse our collection of delicious recipes. No ads, no fluff\u2014just great food you can make at home.", resultsCount: filteredRecipes.length, filters: _jsx(CatalogFilters, { filterGroups: filterGroups, selectedFilters: selectedFilters, onFilterChange: handleFilterChange, onClearAll: handleClearAll }), toolbar: _jsxs("div", { className: "flex items-center gap-3 w-full lg:w-auto", children: [_jsxs(Button, { variant: "outline", onClick: () => setShowCardStack(true), disabled: filteredRecipes.length === 0, className: "border-[#c85a3f]/30 text-[#c85a3f] hover:bg-[#c85a3f]/10 flex-shrink-0", children: [_jsx(Shuffle, { className: "w-4 h-4 mr-2" }), _jsx("span", { className: "hidden sm:inline", children: "Can't Decide?" }), _jsx("span", { className: "sm:hidden", children: "Shuffle" })] }), _jsxs(Select, { value: sortBy, onValueChange: setSortBy, children: [_jsx(SelectTrigger, { className: "w-full sm:w-[180px] border-[#c85a3f]/30", children: _jsx(SelectValue, { placeholder: "Sort by" }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "newest", children: "Newest First" }), _jsx(SelectItem, { value: "title", children: "Alphabetical" }), _jsx(SelectItem, { value: "cookTime", children: "Cook Time" })] })] })] }), children: [_jsx(ActiveFilterChips, { selectedFilters: selectedFilters, filterGroups: filterGroups, onRemoveFilter: (groupId, optionId) => handleFilterChange(groupId, optionId, false), onClearAll: handleClearAll }), _jsx(CatalogGrid, { children: paginatedRecipes.map((recipe) => (_jsx(CatalogCard, { image: recipe.image, title: recipe.title, description: recipe.description, badges: [getDifficultyBadge(recipe.difficulty)], metadata: _jsxs("div", { className: "flex items-center gap-4 text-[#4a4a4a]/70", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Clock, { className: "w-4 h-4" }), _jsxs("span", { children: [recipe.cookTime, " mins"] })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Users, { className: "w-4 h-4" }), _jsxs("span", { children: [recipe.servings, " servings"] })] })] }), onClick: () => handleQuickView(recipe) }, recipe.id))) }), paginatedRecipes.length === 0 && (_jsx("div", { className: "text-center py-12", children: _jsx("p", { className: "text-[#4a4a4a]/70", children: "No recipes found. Try adjusting your filters." }) })), _jsx(CatalogPagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: setCurrentPage })] }), _jsx(Footer, { menuItems: footerMenuItems, siteName: siteName }), _jsx(BottomNav, { onNavigate: onNavigate, onQuizClick: () => setQuizOpen(true), onKitchenHelperClick: () => setKitchenHelperOpen(true), currentPage: "recipes" }), _jsx(GlossaryQuiz, { open: quizOpen, onOpenChange: setQuizOpen }), _jsx(CategoriesSheet, { open: categoriesSheetOpen, onOpenChange: setCategoriesSheetOpen }), _jsx(AISearchSheet, { open: aiSearchOpen, onOpenChange: setAiSearchOpen, onNavigate: onNavigate, onOpenChat: () => {
                    setAiSearchOpen(false);
                    setKitchenHelperOpen(true);
                } }), _jsx(KitchenHelperChat, { open: kitchenHelperOpen, onOpenChange: setKitchenHelperOpen, onNavigate: onNavigate }), showCardStack && (_jsx(RecipeCardStack, { recipes: filteredRecipes, onClose: () => setShowCardStack(false), onNavigate: onNavigate })), showQuickView && selectedRecipe && (_jsx(QuickViewModal, { recipe: selectedRecipe, open: showQuickView, onOpenChange: setShowQuickView }))] }));
}
